import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MedicaidLeadContext from "contexts/MedicaidLeadContext";
import { fetchMedicaidLeads } from "modules/medicaid/actions";
import { useDispatch } from "react-redux";
import PageHeader from "components/PageHeader";
import CollapsableCard from "components/CollapsableCard";
import PaginatedList from "components/PaginatedList";
import {
  EnrichedMedicaidLead,
  MedicaidLeadStatus,
} from "modules/medicaid/types";
import ConsumerMedicaidLeadItem from "./ConsumerMedicaidLeadItem";
import { useSelector } from "redux/reducer";
import TabbedButton, { TabbedButtonType } from "components/TabbedButton";
import { IconType } from "components/Icons";
import { go } from "modules/navigation/helpers/navigator";
import { getPartnerRoute, ROUTE_TEMPLATE } from "modules/navigation/routes";
import { formatPartnerForRoute } from "modules/navigation/helpers/getFormattedPartnerId";

import styles from "./styles.module.scss";
import classNames from "classnames/bind";
import AnimatedLoadingOverlay from "components/AnimatedLoadingOverlay";
import { AnimatedLoadingOverlaySize } from "components/AnimatedLoadingOverlay/types";
import { Redirect } from "react-router-dom";
import { ALL_STATUSES, filterStatus, STATUS_OPTIONS } from "../helpers";
import Dropdown from "components/Forms/Inputs/Dropdown";
import { Stack } from "@mui/material";
import { STATES_MAP } from "resources/options/states";
import Alert, { AlertType } from "components/Alert";

const cx = classNames.bind(styles);

const ConsumerMedicaidLeads = () => {
  const dispatch = useDispatch();
  const { partnerId } = useParams<{ partnerId?: string }>();
  const { availableLeads } = useContext(MedicaidLeadContext);
  const jurisdiction = useSelector((state) => state.state);
  const partner = useSelector((state) => state.partner);
  const userDisplayName = useSelector((state) => state.user?.displayName);

  const [selectedStatus, setSelectedStatus] = useState<
    MedicaidLeadStatus | typeof ALL_STATUSES
  >(ALL_STATUSES);

  const isSupportedState = useMemo<boolean>(
    () =>
      Boolean(
        jurisdiction && partner?.data.jurisdiction.includes(jurisdiction),
      ),
    [partner?.data.jurisdiction, jurisdiction],
  );

  useEffect(() => {
    fetchMedicaidLeads(dispatch);
  }, [dispatch]);

  if (jurisdiction === null) {
    return <Redirect to={ROUTE_TEMPLATE.ROOT} />;
  }

  const handleCreateMedicaidLead = () => {
    if (partnerId) {
      go(
        dispatch,
        getPartnerRoute(
          formatPartnerForRoute(partnerId),
          ROUTE_TEMPLATE.PARTNER_MEDICAID_LEAD_NEW,
        ),
      );
    } else {
      go(dispatch, ROUTE_TEMPLATE.SEARCH);
    }
  };

  if (availableLeads === undefined) {
    return <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.LARGE} />;
  }

  const enrichedMedicaidLeadsArray: EnrichedMedicaidLead[] =
    Object.values(availableLeads);

  const time = new Date().getHours();
  const message =
    time < 12 ? "Good morning" : time < 18 ? "Good afternoon" : "Good evening";

  const fullMessage = [message, userDisplayName].filter(Boolean).join(", ");
  const filteredEnrichedMedicaidLeads = enrichedMedicaidLeadsArray.filter(
    (workflow) => filterStatus(selectedStatus, workflow),
  );

  return (
    <>
      <PageHeader title={fullMessage} />
      <CollapsableCard title="Your Medicaid Qualifications">
        {!isSupportedState && (
          <Alert
            type={AlertType.WARNING}
            message={`${partner?.data.displayName} does not support ${STATES_MAP[jurisdiction]}. To begin creating a new document please change your state or choose another partner.`}
          />
        )}
        <Stack direction="row" className={cx("consumer-medicaid-toolbar")}>
          <div className={cx("consumer-medicaid-filter")}>
            <Dropdown
              name="status"
              options={STATUS_OPTIONS}
              onChange={(value) =>
                setSelectedStatus(
                  value as MedicaidLeadStatus | typeof ALL_STATUSES,
                )
              }
            />
          </div>

          <div className={cx("consumer-medicaid-add-button")}>
            <TabbedButton
              icon={IconType.PLUS}
              label="Add a new medicaid qualification"
              style={TabbedButtonType.SECONDARY}
              onClick={handleCreateMedicaidLead}
              disabled={!isSupportedState}
            />
          </div>
        </Stack>

        <PaginatedList<EnrichedMedicaidLead>
          items={filteredEnrichedMedicaidLeads}
          pageSize={3}
          renderItem={(item) => (
            <ConsumerMedicaidLeadItem
              item={item}
              key={item.medicaidLead.medicaidLeadId}
            />
          )}
        ></PaginatedList>
      </CollapsableCard>
    </>
  );
};

export default ConsumerMedicaidLeads;
